// Orders and receipts (spec/15 P-17): every order placed for the player at any venue, newest first; and one order as
// its receipt, exactly as the counter emails it (ADR-0052): the lines, the offers and codes, the total, how it was
// paid with the cash received and the change, refunds, and the document's number. An unpaid order says to pay at
// the counter.

import type { MyOrder, MyOrderSummary, MyReceiptTenderRow } from '@clavian/api-client';
import type { VenueFormatter } from '@clavian/i18n';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, type BadgeTone, Button, EmptyState } from '@clavian/ui-web';
import { Link, useParams } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { isNotFound } from '../api/problems';
import { useOrder, useOrders } from '../api/queries';
import { formatterFor } from '../lib/format';
import { LoadFailure, RowsSkeleton } from '../shell/feedback';
import { NavIcon } from '../shell/nav-icons';
import { Page } from '../shell/Page';
import { MoneyTabs } from './WalletPage';

const STATUS_TONE: Record<MyOrderSummary['status'], BadgeTone> = {
  placed: 'warn',
  partially_paid: 'warn',
  paid: 'ok',
  partially_refunded: 'info',
  refunded: 'info',
  cancelled: 'neutral',
};

export function OrdersPage(): ReactNode {
  const { t } = useTranslation('player');
  const orders = useOrders();
  const rows = orders.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <Page title={t('orders.title')} description={t('orders.subtitle')}>
      <MoneyTabs current="orders" />
      {orders.isPending ? (
        <div className="pw-panel">
          <RowsSkeleton count={6} />
        </div>
      ) : orders.isError ? (
        <LoadFailure error={orders.error} onRetry={() => void orders.refetch()} />
      ) : rows.length === 0 ? (
        <div className="pw-panel">
          <EmptyState
            icon={<NavIcon name="orders" size={32} />}
            title={t('orders.none')}
            description={t('orders.noneBody')}
            action={
              <Link to="/venues" className="pw-btn pw-btn--primary">
                {t('orders.findVenue')}
              </Link>
            }
          />
        </div>
      ) : (
        <section className="pw-panel">
          <ul className="pw-rows">
            {rows.map((order) => {
              const fmt = formatterFor(order.total.currency);
              return (
                <li key={order.id} className="pw-row pw-row--link">
                  <span className="pw-row__icon" aria-hidden="true">
                    <NavIcon name="orders" size={20} />
                  </span>
                  <Link to="/orders/$saleId" params={{ saleId: order.id }} className="pw-row__main pw-row__link">
                    <span className="pw-row__title">{order.title}</span>
                    <span className="pw-row__meta">
                      {order.venue.name} · {fmt.dateTime(order.placedAt, { date: 'medium', time: 'short' })}
                    </span>
                  </Link>
                  <span className="pw-row__end">
                    <span className="clav-num">{fmt.money(order.total)}</span>
                    <Badge tone={STATUS_TONE[order.status]}>{t(`orders.status.${order.status}`)}</Badge>
                  </span>
                </li>
              );
            })}
          </ul>
          {orders.hasNextPage ? (
            <Button variant="secondary" block busy={orders.isFetchingNextPage} onClick={() => void orders.fetchNextPage()}>
              {t('orders.more')}
            </Button>
          ) : null}
        </section>
      )}
    </Page>
  );
}

function tenderLabel(row: MyReceiptTenderRow, t: (key: string) => string): string {
  switch (row.kind) {
    case 'cash_received':
      return t('orders.detail.cashReceived');
    case 'change':
      return t('orders.detail.change');
    case 'change_to_wallet':
      return t('orders.detail.changeToWallet');
    default:
      return t(`orders.detail.tender.${row.tender ?? 'cash'}`);
  }
}

export function OrderPage(): ReactNode {
  const { t } = useTranslation('player');
  const { saleId } = useParams({ from: '/app/orders/$saleId' });
  const order = useOrder(saleId);

  const back = (
    <Link to="/orders" className="pw-back">
      <NavIcon name="back" size={18} />
      {t('orders.detail.back')}
    </Link>
  );

  if (order.isPending) {
    return (
      <div className="pw-page">
        {back}
        <div className="pw-receipt">
          <RowsSkeleton count={6} />
        </div>
      </div>
    );
  }
  if (order.isError) {
    return (
      <div className="pw-page">
        {back}
        {isNotFound(order.error) ? (
          <EmptyState title={t('orders.detail.notFound')} description={t('orders.detail.notFoundBody')} />
        ) : (
          <LoadFailure error={order.error} onRetry={() => void order.refetch()} />
        )}
      </div>
    );
  }
  return (
    <div className="pw-page">
      {back}
      <Receipt order={order.data} />
    </div>
  );
}

function Receipt({ order }: { readonly order: MyOrder }): ReactNode {
  const { t } = useTranslation('player');
  const fmt: VenueFormatter = formatterFor(order.total.currency, order.venueTimezone);
  const minus = (amount: { amount: number; currency: string }) => `−${fmt.money(amount)}`;
  const unpaid = order.outstanding.amount > 0 && order.status !== 'cancelled';
  const title = order.document === undefined ? t('orders.detail.title') : t(`orders.detail.${order.document.kind}`);

  return (
    <article className="pw-receipt" aria-labelledby="pw-receipt-title">
      <header className="pw-receipt__head">
        <div>
          <p className="pw-receipt__venue">{order.venue.name}</p>
          <h1 className="pw-receipt__title" id="pw-receipt-title">
            {title}
          </h1>
        </div>
        <Badge tone={STATUS_TONE[order.status]}>{t(`orders.status.${order.status}`)}</Badge>
      </header>

      <dl className="pw-receipt__facts">
        {order.document === undefined ? null : (
          <div>
            <dt>{t('orders.detail.number')}</dt>
            <dd className="clav-num">{order.document.formattedNumber}</dd>
          </div>
        )}
        <div>
          <dt>{t('orders.detail.date')}</dt>
          <dd>{fmt.dateTime(order.document?.issuedAt ?? order.placedAt, { date: 'medium', time: 'short' })}</dd>
        </div>
        {order.orderNumber === null ? null : (
          <div>
            <dt>{t('orders.detail.order')}</dt>
            <dd className="clav-num">#{String(order.orderNumber).padStart(4, '0')}</dd>
          </div>
        )}
        {order.document?.gstin == null ? null : (
          <div>
            <dt>{t('orders.detail.gstin')}</dt>
            <dd className="clav-num">{order.document.gstin}</dd>
          </div>
        )}
      </dl>

      {unpaid ? (
        <p className="pw-receipt__due" role="note">
          <strong>
            {t('orders.detail.outstanding')}: <span className="clav-num">{fmt.money(order.outstanding)}</span>
          </strong>
          <span>{t('orders.detail.payAtCounter')}</span>
        </p>
      ) : null}

      <h2 className="pw-receipt__section">{t('orders.detail.items')}</h2>
      <ul className="pw-receipt__lines">
        {order.lines.map((line, index) => (
          <li key={`${line.name}-${index}`} className="pw-receipt__line">
            <span className="pw-receipt__qty clav-num">{line.quantity}×</span>
            <span className="pw-receipt__name">
              {line.name}
              {line.modifiers.length > 0 || line.quantity > 1 ? (
                <span className="pw-subtle">
                  {[line.modifiers.join(', '), line.quantity > 1 ? t('orders.detail.each', { amount: fmt.money(line.unitPrice) }) : '']
                    .filter((part) => part !== '')
                    .join(' · ')}
                </span>
              ) : null}
            </span>
            <span className="pw-receipt__amount clav-num">{fmt.money(line.total)}</span>
          </li>
        ))}
      </ul>

      <dl className="pw-receipt__totals">
        {order.promotions.length + order.discounts.length > 0 ? (
          <div>
            <dt>{t('orders.detail.subtotal')}</dt>
            <dd className="clav-num">{fmt.money(order.gross)}</dd>
          </div>
        ) : null}
        {order.promotions.map((promotion) => (
          <div key={promotion.name}>
            <dt>{t('orders.detail.promotion', { name: promotion.name })}</dt>
            <dd className="clav-num">{minus(promotion.amount)}</dd>
          </div>
        ))}
        {order.discounts.map((discount, index) => (
          <div key={`${discount.code ?? 'manual'}-${index}`}>
            <dt>{discount.code === null ? t('orders.detail.discountManual') : t('orders.detail.discountCode', { code: discount.code })}</dt>
            <dd className="clav-num">{minus(discount.amount)}</dd>
          </div>
        ))}
        <div className="pw-receipt__total">
          <dt>{t('orders.detail.total')}</dt>
          <dd className="clav-num">{fmt.money(order.total)}</dd>
        </div>
        {order.document !== undefined && order.document.taxComponents.length > 0
          ? order.document.taxComponents.map((row) => (
              <div key={`${row.component}-${row.rateBps}`} className="pw-receipt__tax">
                <dt>{t('orders.detail.taxLine', { component: row.component.toUpperCase(), rate: String(row.rateBps / 100) })}</dt>
                <dd className="clav-num">{fmt.money(row.amount)}</dd>
              </div>
            ))
          : null}
      </dl>

      {order.tenders.length === 0 ? null : (
        <>
          <h2 className="pw-receipt__section">{t('orders.detail.paid')}</h2>
          <dl className="pw-receipt__totals">
            {order.tenders.map((row, index) => (
              <div key={`${row.kind}-${index}`} className={row.nested ? 'pw-receipt__nested' : undefined}>
                <dt>{tenderLabel(row, t)}</dt>
                <dd className="clav-num">{fmt.money(row.amount)}</dd>
              </div>
            ))}
          </dl>
        </>
      )}

      {order.refunds.length === 0 ? null : (
        <>
          <h2 className="pw-receipt__section">{t('orders.detail.refunds')}</h2>
          <dl className="pw-receipt__totals">
            {order.refunds.map((refund, index) => (
              <div key={`${refund.at}-${index}`}>
                <dt>
                  {t('orders.detail.refund', { tender: t(`orders.detail.tender.${refund.tender}`) })}
                  {refund.status === 'pending_approval' ? <span className="pw-subtle"> · {t('orders.detail.refundPending')}</span> : null}
                </dt>
                <dd className="clav-num">{fmt.money(refund.amount)}</dd>
              </div>
            ))}
          </dl>
        </>
      )}

      {order.document === undefined ? <p className="pw-subtle">{t('orders.detail.noDocument')}</p> : null}
    </article>
  );
}
